import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageSquare, X, Send, Sparkles, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/concierge/chat`;
const GREET = "Hi! I'm the Virasat Retreat AI Concierge — ask me about rooms, rates, meal plans, or start a booking.";

export const ConciergeChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", text: GREET }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState(null);
  const [booked, setBooked] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput(""); setBusy(true);
    try {
      const { data } = await axios.post(API, { session_id: session, message: text });
      setSession(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
      if (data.booking_created) setBooked(true);
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "Sorry, I couldn&rsquo;t reach the desk just now. Please try WhatsApp: +91 77650 90955." }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      {!open && (
        <button
          data-testid="concierge-open"
          onClick={() => setOpen(true)}
          className="fixed bottom-24 right-6 z-50 h-14 w-14 rounded-full bg-[#B89965] text-[#0D120E] flex items-center justify-center shadow-xl hover:scale-110 transition-transform ring-1 ring-[#0D120E]/20"
          aria-label="Open AI Booking Concierge"
        >
          <Sparkles size={22} />
        </button>
      )}

      {open && (
        <div
          data-testid="concierge-panel"
          className="fixed bottom-6 right-6 z-50 w-[92vw] sm:w-[400px] h-[560px] max-h-[80vh] bg-[#0D120E] text-[#F5F2ED] border border-[#B89965]/40 rounded-lg shadow-2xl flex flex-col overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-[#F5F2ED]/10 flex items-center justify-between bg-[#111a13]">
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-[#B89965]" />
              <div>
                <p className="font-display text-base leading-none">AI Booking Concierge</p>
                <p className="text-[10px] uppercase tracking-wider text-[#F5F2ED]/50 mt-1">Hotel Virasat Retreat</p>
              </div>
            </div>
            <button data-testid="concierge-close" onClick={() => setOpen(false)} className="text-[#F5F2ED]/60 hover:text-[#F5F2ED]"><X size={18} /></button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="concierge-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap rounded-lg ${m.role === "user" ? "bg-[#B89965] text-[#0D120E]" : "bg-[#1a231d] text-[#F5F2ED]/90"}`}>{m.text}</div>
              </div>
            ))}
            {busy && <div className="flex justify-start"><div className="bg-[#1a231d] px-3.5 py-2.5 rounded-lg text-sm text-[#F5F2ED]/60"><Loader2 size={14} className="inline animate-spin mr-2" />Concierge is typing…</div></div>}
            {booked && (
              <div data-testid="concierge-booked" className="mt-3 p-3 rounded-lg bg-[#2C402E]/60 border border-[#B89965]/40 text-sm flex items-start gap-2">
                <CheckCircle2 size={16} className="text-[#B89965] shrink-0 mt-0.5" />
                <span>Your booking request has been sent to the front desk. We'll confirm shortly.</span>
              </div>
            )}
            <div ref={endRef} />
          </div>

          <form onSubmit={send} className="p-3 border-t border-[#F5F2ED]/10 flex gap-2 bg-[#111a13]">
            <input
              data-testid="concierge-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about rooms, rates or say 'book a room'…"
              className="flex-1 bg-transparent border border-[#F5F2ED]/20 rounded-full px-4 py-2 text-sm placeholder-[#F5F2ED]/40 focus:outline-none focus:border-[#B89965]"
            />
            <button data-testid="concierge-send" disabled={busy || !input.trim()} className="h-9 w-9 rounded-full bg-[#B89965] text-[#0D120E] flex items-center justify-center disabled:opacity-50 hover:bg-[#c9aa76] transition-colors" aria-label="Send">
              <Send size={15} />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

/* Compatibility export in case anyone imports ConciergeButton */
export const ConciergeButton = ConciergeChat;
